import React, { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  CreateUpdateContactDto,
  GetClientDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import FormRow from '../../../app/components/FormRow';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { ClientSelector } from '../Selects/ClientSelector';
import { Option } from '../Selects/Selector';
import DictionarySelector from '../Selects/DictionarySelector';

interface ContactEditorProps {
  id?: string;
  contact: CreateUpdateContactDto;
  handleClose?: () => void;
  show?: boolean;
  client?: GetClientDto;
  onCompleted: (
    contact: CreateUpdateContactDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ContactEditor: React.FC<ContactEditorProps> = props => {
  const [contact, setContact] = useState(props.contact);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('clientLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setContact(props.contact);
    }
  }, [props.contact, props.show]);

  const createContact = async () => {
    setIsLoading(true);
    props
      .onCompleted(contact, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(async errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const propertyChange = (obj: Partial<CreateUpdateContactDto>) => {
    setContact({ ...contact, ...obj });
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id ? t(tk.contact.edit) : t(tk.contact.add)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="d-grid gap-4 mb-3">
            <FormRow controlId="client" label={t(tk.client.client)}>
              <ClientSelector
                onChange={e => {
                  if (propertyChange) {
                    propertyChange({
                      clientId: (e as Option).value,
                    });
                  }
                }}
                value={props?.client}
                readOnly={true}
              />
            </FormRow>
            <FormRow controlId="contactType" label={t(tk.contact.contactType)}>
              <DictionarySelector
                dictionary="Typy kontaktów"
                value={contact.contactTypeId}
                onChange={option =>
                  propertyChange({
                    contactTypeId: option.value,
                  })
                }
              />
            </FormRow>
            <FormRow controlId="email" label={t(tk.contact.email)}>
              <Form.Control
                type="email"
                value={contact.email || ''}
                onChange={e => propertyChange({ email: e.target.value })}
              />
            </FormRow>
            <FormRow controlId="active" label={t(tk.common.activeHe)}>
              <Form.Check
                type="checkbox"
                className="pt-2"
                checked={contact.active ?? true}
                onChange={e => propertyChange({ active: e.target.checked })}
              />
            </FormRow>
          </Form>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo id={props.id} method={pok.contacts.getLastEditor} />
          )}
          <Button
            variant="outline-secondary"
            onClick={props.handleClose}
            disabled={isLoading}
          >
            {t(tk.button.close)}
          </Button>
          <Button
            variant="primary"
            onClick={createContact}
            isLoading={isLoading}
          >
            {t(tk.button.save)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
